import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import Toast, { ToastProps } from './Toast';
import './ToastService.scss';
import SuccessIcon from '../Icons/SuccessIcon';
import AlertIcon from '../Icons/AlertIcon';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { registerToastEventListener } from './toastBridge';

type ToastConfig = ToastProps & { id: string };

type ShowToastOptions = Partial<Omit<ToastProps, 'isVisible' | 'onClose'>>;

interface ToastServiceType {
  showToast: (options: ShowToastOptions) => void;
  showSuccessToast: (headerMessage: string, message?: string) => void;
  showErrorToast: (headerMessage: string, message?: string) => void;
  showWarningToast: (headerMessage: string, message?: string) => void;
  showInfoToast: ({ headerMessage, message, icon }: { headerMessage: string; message?: string; icon?: any }) => void;
  hideToast: (id: string) => void;
}

const ToastServiceContext = createContext<ToastServiceType | undefined>(undefined);

export const useToastService = () => {
  const context = useContext(ToastServiceContext);
  if (!context) throw new Error('useToastService must be used within ToastServiceProvider');
  return context;
};

export const ToastServiceProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastConfig[]>([]);

  // Register a listener for toast events from the bridge
  useEffect(() => {
    const unregister = registerToastEventListener((event) => {
      switch (event.type) {
        case 'success':
          showSuccessToast(event.headerMessage, event.message);
          break;
        case 'error':
          showErrorToast(event.headerMessage, event.message);
          break;
        case 'warning':
          showWarningToast(event.headerMessage, event.message);
          break;
        case 'info':
          showInfoToast({ headerMessage: event.headerMessage, message: event.message });
          break;
      }
    });

    // Clean up the listener when the component unmounts
    return unregister;
  }, []);

  const showToast = (options: ShowToastOptions) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: ToastConfig = {
      ...options,
      isVisible: true,
      onClose: () => hideToast(id),
      id,
    } as ToastConfig;

    setToasts((prev) => [...prev, newToast]);

    if (options.autoClose && options.autoCloseTime) {
      setTimeout(() => hideToast(id), options.autoCloseTime);
    }
  };

  const showSuccessToast = (headerMessage: string, message = '') => {
    showToast({
      icon: <SuccessIcon />,
      headerMessage,
      headerColor: '#32D583',
      message,
      messageColor: '#B8C5CC',
      autoClose: true,
      autoCloseTime: 5000,
      showCloseButton: false,
    });
  };
  const showInfoToast = ({
    headerMessage,
    message = '',
    icon,
  }: {
    headerMessage: string;
    message?: string;
    icon?: any;
  }) => {
    showToast({
      icon: icon || <ArrowPathIcon color={'#60BFDA'} />,
      headerMessage,
      headerColor: '#60BFDA',
      message,
      messageColor: '#60BFDA',
      autoClose: true,
      autoCloseTime: 5000,
      showCloseButton: false,
    });
  };

  const showErrorToast = (headerMessage: string, message?: string) => {
    showToast({
      icon: <AlertIcon />,
      headerMessage,
      headerColor: '#F97066',
      message,
      messageColor: '#B8C5CC',
      autoClose: true,
      autoCloseTime: 10000,
      showCloseButton: true,
    });
  };

  const showWarningToast = (headerMessage: string, message?: string) => {
    showToast({
      icon: <AlertIcon color="#F0B429" />,
      headerMessage,
      headerColor: '#F0B429',
      message,
      messageColor: '#B8C5CC',
      autoClose: true,
      autoCloseTime: 5000,
      showCloseButton: true,
    });
  };

  const hideToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastServiceContext.Provider value={{ showToast, showSuccessToast, showErrorToast, showWarningToast, hideToast, showInfoToast }}>
      {children}
      <div className='toast-stack-container'>
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} />
        ))}
      </div>
    </ToastServiceContext.Provider>
  );
};
